import React, { useState, useEffect } from 'react';
import { apiClient } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings, Save, RotateCcw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CONFIG_LABELS = {
    priceWeight: { label: 'Poids du Prix', description: 'Influence des préférences de prix', max: 0.5 },
    colorWeight: { label: 'Poids des Couleurs', description: 'Influence des préférences de couleur', max: 0.5 },
    brandVisitWeight: { label: 'Poids Marque Visitée', description: 'Bonus pour marques déjà visitées', max: 0.5 },
    searchBoostWeight: { label: 'Poids Recherches', description: 'Influence des recherches récentes', max: 0.5 },
    popularityWeight: { label: 'Poids Popularité', description: 'Influence de la popularité globale', max: 0.5 },
    imageViewWeight: { label: 'Poids Vue Images (par image)', description: 'Score par image vue', max: 2.0, step: 0.1 },
    engagementMultiplier: { label: 'Multiplicateur Engagement', description: 'Bonus pour utilisateurs engagés', max: 2.0, min: 1.0 },
    brandQuota: { label: 'Quota par Marque', description: 'Produits max par marque dans le pool', max: 50, step: 1, isInt: true },
    diversifyWindow: { label: 'Fenêtre Diversification', description: 'Taille fenêtre anti-répétition', max: 20, step: 1, isInt: true },
    diversifyMaxPerBrand: { label: 'Max par Marque (Fenêtre)', description: 'Max produits même marque dans fenêtre', max: 10, step: 1, isInt: true }
};

const DEFAULTS = {
    priceWeight: 0.10,
    colorWeight: 0.10,
    brandVisitWeight: 0.10,
    searchBoostWeight: 0.25,
    popularityWeight: 0.15,
    imageViewWeight: 0.50,
    engagementMultiplier: 1.05,
    brandQuota: 20,
    diversifyWindow: 5,
    diversifyMaxPerBrand: 1
};

export default function AlgorithmConfigPage() {
    const [config, setConfig] = useState(DEFAULTS);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            const data = await apiClient.admin.getAlgorithmConfig();
            const parsed = {};
            for (const [key, val] of Object.entries(data)) {
                parsed[key] = parseFloat(val);
            }
            setConfig(prev => ({ ...prev, ...parsed }));
        } catch (error) {
            // console.error('Failed to load config:', error);
            toast.error('Erreur de chargement de la configuration');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (key, value) => {
        const meta = CONFIG_LABELS[key];
        const parsed = meta?.isInt ? Math.round(parseFloat(value)) : parseFloat(value);
        setConfig(prev => ({ ...prev, [key]: parsed }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await apiClient.admin.saveAlgorithmConfig(config);
            toast.success('Configuration sauvegardée !');
            setHasChanges(false);
        } catch (error) {
            // console.error('Failed to save config:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        setConfig(DEFAULTS);
        setHasChanges(true);
    };

    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-8 bg-gray-50">
            <div className="max-w-4xl mx-auto">
                <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Settings className="w-8 h-8 text-amber-600" />
                            Configuration Algorithme
                        </h1>
                        <p className="text-gray-600 mt-1">Ajustez les paramètres de recommandation</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Défaut
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={isSaving || !hasChanges}
                            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-sm"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                        </Button>
                    </div>
                </div>

                {hasChanges && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-800">
                        <AlertCircle className="w-5 h-5" />
                        <span>Vous avez des modifications non sauvegardées</span>
                    </div>
                )}

                <div className="grid gap-4">
                    {Object.entries(CONFIG_LABELS).map(([key, meta]) => {
                        const value = config[key] ?? DEFAULTS[key];
                        const min = meta.min ?? 0;
                        const max = meta.max ?? 1;
                        const step = meta.step ?? 0.01;

                        return (
                            <Card key={key} className="overflow-hidden">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <h3 className="font-semibold text-gray-900 dark:text-white">{meta.label}</h3>
                                            <p className="text-sm text-gray-500">{meta.description}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-2xl font-bold text-amber-600">
                                                {meta.isInt ? Math.round(value) : value.toFixed(2)}
                                            </span>
                                            {!meta.isInt && <span className="text-gray-400 ml-1">/ {max}</span>}
                                        </div>
                                    </div>
                                    <div className="pt-2">
                                        <input
                                            type="range"
                                            value={value}
                                            onChange={(e) => handleChange(key, e.target.value)}
                                            min={min}
                                            max={max}
                                            step={step}
                                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                                            style={{
                                                background: `linear-gradient(to right, #d97706 0%, #d97706 ${((value - min) / (max - min)) * 100}%, #e5e7eb ${((value - min) / (max - min)) * 100}%, #e5e7eb 100%)`
                                            }}
                                        />
                                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                                            <span>{min}</span>
                                            <span>{max}</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

